/**
 * OverrideAggregatesAdmin Component (HG-3B G3-03).
 *
 * Purpose: the GCH-13a aggregate override report. Grade overrides summarised by
 * competency, by instructor and by category, with counts and rates.
 *
 * The numbers are not computed here. They come from one query against
 * public.v_assessment_override_aggregates (migration 250), so the definition of
 * the population lives in one place and this surface cannot drift from it. The
 * view is security_invoker, so an administrator sees exactly the airlines their
 * own RLS grants them.
 *
 * Two things this screen states rather than implies, because a rate whose
 * denominator is unstated is not evidence:
 *   - what is inside the population (live gradings that carried a proposed
 *     grade) and what is outside it (historical rows per ICD 9.3, and gradings
 *     recorded with no proposal shown per GCH-13);
 *   - which denominator each grouping uses, since the category grouping
 *     measures a share of the overrides and the other two measure a share of
 *     the gradings.
 *
 * Permissions: Super Admin only, via the /admin/override-aggregates route
 * wrapper. No charts, by design: a sortable table is the deliverable.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { BarChart3, Loader, RefreshCw } from 'lucide-react';

type Grouping = 'competency' | 'instructor' | 'category';

/** Only what the airline filter needs; the full Airline type is not selected. */
interface AirlineOption {
  id: string;
  name: string;
}

type PopulationBasis = 'gradings_with_proposal' | 'overrides_in_scope';

interface AggregateRow {
  grouping: Grouping;
  org_id: string;
  group_key: string;
  group_label: string | null;
  population: number;
  overrides: number;
  population_basis: PopulationBasis;
  rate: number | null;
}

type SortKey = 'group_label' | 'population' | 'overrides' | 'rate';

const GROUPING_TABS: Array<{ key: Grouping; label: string; caption: string }> = [
  {
    key: 'competency',
    label: 'By competency',
    caption:
      'Share of gradings that carried a proposed grade in which the instructor recorded a different grade.',
  },
  {
    key: 'instructor',
    label: 'By instructor',
    caption:
      'Same measure, grouped by the instructor who ran the session. The override actor is recorded per row and may differ.',
  },
  {
    key: 'category',
    label: 'By category',
    caption:
      'Share of the overrides themselves. A category exists only on an override (DEC-28), so the denominator here is the overrides in scope, not all gradings.',
  },
];

/** ICD 8.4 OverrideCategoryV1, in the wording Step 6 offers. */
const CATEGORY_LABELS: Record<string, string> = {
  evidence_not_captured_by_dimensions: 'Evidence not captured by the dimensions',
  dimension_recorded_in_error: 'Dimension recorded in error',
  tem_outcome_not_attributable: 'TEM outcome not attributable to the trainee',
  other: 'Other',
};

function formatRate(rate: number | null): string {
  // Null means an empty population. It is not zero, and it is not written as
  // zero (GCH-35 applied to a derived value).
  if (rate == null) return 'Not applicable';
  return `${(rate * 100).toFixed(1)}%`;
}

export const OverrideAggregatesAdmin: React.FC = () => {
  const [rows, setRows] = useState<AggregateRow[]>([]);
  const [airlines, setAirlines] = useState<AirlineOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [grouping, setGrouping] = useState<Grouping>('competency');
  const [orgFilter, setOrgFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('rate');
  const [sortAsc, setSortAsc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [aggregateResult, airlineResult] = await Promise.all([
        supabase.from('v_assessment_override_aggregates').select('*'),
        supabase.from('airlines').select('id, name').order('name'),
      ]);

      if (aggregateResult.error) throw aggregateResult.error;
      setRows((aggregateResult.data ?? []) as AggregateRow[]);
      setAirlines((airlineResult.data ?? []) as AirlineOption[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load override aggregates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const airlineName = useCallback(
    (orgId: string): string => airlines.find((a) => a.id === orgId)?.name ?? orgId,
    [airlines],
  );

  const displayLabel = useCallback(
    (row: AggregateRow): string => {
      if (row.grouping === 'category') {
        return CATEGORY_LABELS[row.group_key] ?? row.group_key;
      }
      return row.group_label ?? row.group_key;
    },
    [],
  );

  const visibleRows = useMemo(() => {
    const filtered = rows.filter(
      (r) => r.grouping === grouping && (orgFilter === 'all' || r.org_id === orgFilter),
    );

    const direction = sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'group_label') {
        return direction * displayLabel(a).localeCompare(displayLabel(b));
      }
      const av = sortKey === 'rate' ? (a.rate ?? -1) : a[sortKey];
      const bv = sortKey === 'rate' ? (b.rate ?? -1) : b[sortKey];
      return direction * (av - bv);
    });
  }, [rows, grouping, orgFilter, sortKey, sortAsc, displayLabel]);

  const totals = useMemo(() => {
    // Only the two gradings-based groupings can be totalled: summing the
    // category grouping would count each override once per category row against
    // a denominator that is already the override count.
    if (grouping === 'category') return null;
    return visibleRows.reduce(
      (acc, r) => ({
        population: acc.population + r.population,
        overrides: acc.overrides + r.overrides,
      }),
      { population: 0, overrides: 0 },
    );
  }, [visibleRows, grouping]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc((prev) => !prev);
      return;
    }
    setSortKey(key);
    setSortAsc(key === 'group_label');
  };

  const activeTab = GROUPING_TABS.find((t) => t.key === grouping);
  const showOrgColumn = orgFilter === 'all';

  const sortIndicator = (key: SortKey) => (key === sortKey ? (sortAsc ? ' ▲' : ' ▼') : '');

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Grade override reporting
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Where an instructor recorded a grade other than the one the weakest-dimension rule
            proposed. Inside the population: live gradings that carried a proposed grade. Outside
            it, and never counted as zero: assessments recorded before the dimensions existed, and
            gradings recorded with no proposal shown.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-navy hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <nav className="flex gap-1" aria-label="Grouping">
          {GROUPING_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setGrouping(tab.key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                grouping === tab.key
                  ? 'bg-navy text-white'
                  : 'border border-gray-300 text-navy hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <label className="ml-auto flex items-center gap-2 text-sm text-gray-600">
          Airline
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="all">All airlines</option>
            {airlines.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {activeTab && <p className="mb-3 text-xs text-gray-500">{activeTab.caption}</p>}

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-gray-500">
          <Loader className="w-5 h-5 animate-spin" />
          Loading override aggregates...
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="rounded border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600">
          No gradings with a proposed grade in this scope yet. Overrides can only be reported once
          Step 6 has recorded dimensions on at least one assessment.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {showOrgColumn && (
                  <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Airline</th>
                )}
                <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">
                  <button onClick={() => toggleSort('group_label')} className="hover:underline">
                    {grouping === 'competency'
                      ? 'Competency'
                      : grouping === 'instructor'
                        ? 'Instructor'
                        : 'Category'}
                    {sortIndicator('group_label')}
                  </button>
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 border-b">
                  <button onClick={() => toggleSort('overrides')} className="hover:underline">
                    Overrides{sortIndicator('overrides')}
                  </button>
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 border-b">
                  <button onClick={() => toggleSort('population')} className="hover:underline">
                    {grouping === 'category' ? 'Overrides in scope' : 'Gradings with a proposal'}
                    {sortIndicator('population')}
                  </button>
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 border-b">
                  <button onClick={() => toggleSort('rate')} className="hover:underline">
                    {grouping === 'category' ? 'Share of overrides' : 'Override rate'}
                    {sortIndicator('rate')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={`${row.grouping}:${row.org_id}:${row.group_key}`}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  {showOrgColumn && (
                    <td className="px-3 py-2 text-gray-600">{airlineName(row.org_id)}</td>
                  )}
                  <td className="px-3 py-2 font-medium text-navy">{displayLabel(row)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.overrides}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.population}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatRate(row.rate)}</td>
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td
                    className="px-3 py-2 font-medium text-navy"
                    colSpan={showOrgColumn ? 2 : 1}
                  >
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {totals.overrides}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {totals.population}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {formatRate(
                      totals.population > 0 ? totals.overrides / totals.population : null,
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default OverrideAggregatesAdmin;
